import {
  ArrowLittleDownIcon,
  CheckIcon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@joinrepublic/dls-ui-kit';

import { ALL_SECTORS_LABEL } from '../constants';
import { MENU_MAX_HEIGHT } from './constants';
import { ISectorMenuProps } from './types';

/**
 * Mobile face of the sector filter — the same control style the design gives
 * sorting, so the two sit on one row. The chip row it replaces survives from
 * `md` up; see `SectorFilter`, which owns the shared selection.
 *
 * Presentational and stateless: `SectorFilter` holds the slug since the chip
 * row is mounted at the same time and the two must never disagree.
 */
export const SectorMenu = ({
  sectors,
  selectedSlug,
  onSelect,
}: ISectorMenuProps) => {
  const selectedLabel =
    sectors.find((sector) => sector.slug === selectedSlug)?.displayName ??
    ALL_SECTORS_LABEL;

  return (
    <Menu placement="bottom-start" isLazy>
      {/* The visible label is just a sector name, which says nothing about the
          control's purpose (unlike `SortByMenu`'s own "Sort by: …"). This also
          keeps the accessible name distinct from the "All" chip, mounted at
          the same time. */}
      <MenuButton
        type="button"
        aria-label={`Sector: ${selectedLabel}`}
        display="inline-flex"
        alignItems="center"
        gap="1"
        minWidth="0"
        maxWidth="full"
      >
        {/* The longest sector name plus the sort trigger beside it overruns a
            320px viewport, so the label truncates rather than pushing the row
            wider than the screen. */}
        <Text
          as="span"
          size="body-sm-md"
          variant="text-secondary"
          minWidth="0"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {selectedLabel}
        </Text>
        <ArrowLittleDownIcon
          boxSize="5"
          color="neutral.fg-secondary"
          flexShrink={0}
        />
      </MenuButton>

      {/* A dozen sectors plus "All" outgrow a phone viewport, and `MenuList`
          does not scroll on its own. */}
      <MenuList maxHeight={MENU_MAX_HEIGHT} overflowY="auto">
        <MenuItem onClick={() => onSelect(null)}>
          {ALL_SECTORS_LABEL}
          {selectedSlug === null && <CheckIcon ml="auto" />}
        </MenuItem>

        {sectors.map((sector) => (
          <MenuItem key={sector.slug} onClick={() => onSelect(sector.slug)}>
            {sector.displayName}
            {sector.slug === selectedSlug && <CheckIcon ml="auto" />}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
